import { createSignal, onMount, Show, type JSX } from 'solid-js';
import { isServer } from 'solid-js/web';
import { authStore } from '@stores/session';

// Modal interstitial shown once per browser to unauthenticated visitors.
// Requires a positive 18+ confirmation; declining redirects off-site.
//
// Signed-in users bypass the gate entirely — they've either already seen
// it on a previous unauthenticated visit or they signed in from a
// trusted entry point (magic-link email). Per product call (phase B
// clarification #4) there are no legacy accounts to re-prompt.
//
// The flag lives in localStorage rather than on the profile because the
// whole point is to gate BEFORE the user authenticates.

const STORAGE_KEY = 'tt_age_gate_accepted_v1';

export default function AgeGate(): JSX.Element {
  // Default to not-visible on SSR and during initial hydration, then
  // flip to visible inside onMount if the conditions are met. This
  // avoids a flash of the modal on every page load for users who have
  // already accepted.
  const [visible, setVisible] = createSignal(false);

  onMount(() => {
    if (isServer) return;
    if (authStore.user()) return;
    try {
      if (localStorage.getItem(STORAGE_KEY) === '1') return;
    } catch {
      // Private-mode Safari throws on localStorage access. Fall through
      // and show the gate — defensible default.
    }
    setVisible(true);
  });

  function accept() {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // If we can't persist (private mode), the modal will re-appear on
      // next visit. Acceptable trade-off.
    }
    setVisible(false);
  }

  function decline() {
    // Hard redirect off-site per product requirement. Do NOT store any
    // "declined" state — a declined user should never see the app again
    // in this session.
    window.location.href = 'https://google.co.uk';
  }

  return (
    <Show when={visible()}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="age-gate-title"
        class="fixed inset-0 bg-brand-ink/60 backdrop-blur-sm flex items-center justify-center p-4 z-[60]"
      >
        <div class="bg-brand-cream rounded-2xl max-w-md p-6 shadow-2xl">
          <h2 id="age-gate-title" class="font-display text-2xl font-bold">
            Are you 18 or over?
          </h2>
          <p class="mt-4 text-sm">
            Terp Track is an informational patient support tool for tracking personal medication
            efficacy and does not constitute medical advice.
          </p>
          <p class="mt-3 text-sm">
            You must be 18 or older to continue.
          </p>
          <div class="mt-6 flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={accept}
              class="flex-1 rounded-full bg-brand-green text-brand-cream py-3 font-medium cursor-pointer hover:bg-brand-moss transition-colors"
            >
              Yes, I'm 18+
            </button>
            <button
              type="button"
              onClick={decline}
              class="flex-1 rounded-full border border-brand-hex text-brand-green py-3 font-medium cursor-pointer hover:bg-brand-hex/40 transition-colors"
            >
              No, I'm not 18
            </button>
          </div>
        </div>
      </div>
    </Show>
  );
}
